import React from "react";
import { Mail, MapPin, Clock, Phone } from "lucide-react";
import SEO from "../components/SEO";
import SectionReveal from "../components/SectionReveal";
import ContactForm from "../components/forms/ContactForm";
import { SEO as META, SITE } from "../content";

export default function Contact() {
  return (
    <>
      <SEO {...META.contact} />

      {/* HERO */}
      <section
        className="relative bg-ridge-charcoal text-white"
        data-testid="contact-hero"
      >
        <div className="container-pad py-20 md:py-24 lg:py-28">
          <SectionReveal>
            <div className="max-w-3xl">
              <span className="text-xs font-semibold uppercase tracking-[0.22em] text-ridge-amber">
                Contact
              </span>
              <h1 className="font-display mt-4 text-4xl font-bold leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl">
                Let's Move
              </h1>
              <p className="mt-5 max-w-xl text-base leading-relaxed text-white/85 md:text-lg">
                Get a quote, ask a question, or set up a dedicated route — we
                respond within one business hour.
              </p>
            </div>
          </SectionReveal>
        </div>
        <div className="absolute inset-x-0 bottom-0 h-1 bg-ridge-amber" />
      </section>

      {/* FORM + INFO */}
      <section className="section-pad bg-white" data-testid="contact-form-section">
        <div className="container-pad">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-14">
            <SectionReveal className="lg:col-span-8">
              <ContactForm />
            </SectionReveal>

            <SectionReveal className="lg:col-span-4" delay={0.08}>
              <aside
                className="sticky top-28 space-y-6 rounded-md border border-ridge-border bg-ridge-stone p-6 md:p-8"
                data-testid="contact-info-aside"
              >
                <div>
                  <span className="text-xs font-semibold uppercase tracking-[0.2em] text-ridge-amber">
                    Reach us directly
                  </span>
                  <h2 className="font-display mt-3 text-2xl font-bold leading-tight tracking-tight text-ridge-charcoal md:text-3xl">
                    We respond within one business hour.
                  </h2>
                </div>

                <ul className="space-y-5">
                  <li className="flex items-start gap-3">
                    <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-ridge-charcoal text-white">
                      <Phone className="h-4 w-4" aria-hidden="true" />
                    </span>
                    <span>
                      <span className="block text-xs font-semibold uppercase tracking-[0.16em] text-ridge-charcoal">
                        Phone
                      </span>
                      <span
                        className="mt-1 block text-sm font-medium text-ridge-text"
                        data-testid="contact-phone-info"
                      >
                        Contact via form
                      </span>
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-ridge-charcoal text-white">
                      <Mail className="h-4 w-4" aria-hidden="true" />
                    </span>
                    <span>
                      <span className="block text-xs font-semibold uppercase tracking-[0.16em] text-ridge-charcoal">
                        Email
                      </span>
                      <a
                        href={`mailto:${SITE.email}`}
                        className="mt-1 block text-sm font-medium text-ridge-charcoal transition-colors hover:text-ridge-amber"
                        data-testid="contact-email-info"
                      >
                        {SITE.email}
                      </a>
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-ridge-charcoal text-white">
                      <MapPin className="h-4 w-4" aria-hidden="true" />
                    </span>
                    <span>
                      <span className="block text-xs font-semibold uppercase tracking-[0.16em] text-ridge-charcoal">
                        Location
                      </span>
                      <span className="mt-1 block text-sm font-medium text-ridge-text">
                        {SITE.city}, Canada
                      </span>
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-ridge-charcoal text-white">
                      <Clock className="h-4 w-4" aria-hidden="true" />
                    </span>
                    <span>
                      <span className="block text-xs font-semibold uppercase tracking-[0.16em] text-ridge-charcoal">
                        Hours
                      </span>
                      <span className="mt-1 block text-sm font-medium text-ridge-text">
                        {SITE.hours}
                      </span>
                      <span className="block text-xs text-ridge-muted">
                        Saturday by arrangement.
                      </span>
                    </span>
                  </li>
                </ul>

                <div className="border-t border-ridge-border pt-5">
                  <p className="text-xs leading-relaxed text-ridge-muted">
                    For urgent same-day requests outside business hours, please
                    email us directly — we monitor the inbox closely.
                  </p>
                </div>
              </aside>
            </SectionReveal>
          </div>
        </div>
      </section>
    </>
  );
}
